import { gql, useEnhancedMutation } from "@uplift-ltd/apollo";
import React, { useState } from "react";
import Button from "components/common/Button";
import { Input } from "components/common/Form";
import {
  ApplyProductCouponMutation,
  ApplyProductCouponVariables,
} from "./__generated__/DiscountCodeInput";
import styles from "./DiscountCodeInput.module.css";

export interface DiscountData {
  validated: boolean;
  newPrice: number;
  code: string;
}

interface Props {
  productId: string;
  productVariationId?: string;
  onValidate: (data: DiscountData) => void;
}

const APPLY_COUPON = gql`
  mutation ApplyProductCoupon($productId: ID!, $couponCode: String!, $productVariationId: ID) {
    applyProductCoupon(
      input: {
        clientMutationId: "ApplyProductCoupon"
        productId: $productId
        productVariationId: $productVariationId
        couponCode: $couponCode
      }
    ) {
      success
      message
      priceUSD
      errorCode
    }
  }
`;

const DiscountCodeInput: React.FC<Props> = ({ productId, productVariationId, onValidate }) => {
  const [discountCode, setDiscountCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showButton, setShowButton] = useState(true);

  const [applyCoupon, { data: couponData, loading }] = useEnhancedMutation<
    ApplyProductCouponMutation,
    ApplyProductCouponVariables
  >(APPLY_COUPON);

  const validateCoupon = async () => {
    try {
      if (discountCode) {
        setErrorMessage("");

        const response = await applyCoupon({
          variables: {
            couponCode: discountCode,
            productVariationId,
            productId,
          },
        });

        if (!response.data?.applyProductCoupon) {
          throw new Error("Unable to validate Coupon");
        }

        if (response?.data?.applyProductCoupon?.success === false) {
          throw new Error("Invalid Coupon");
        }

        onValidate({
          validated: response.data.applyProductCoupon.success,
          newPrice: response.data.applyProductCoupon.priceUSD,
          code: discountCode,
        });
        setShowButton(false);
      } else {
        throw new Error("Please add a coupon to validate");
      }
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "An error occurred while validating the Coupon."
      );
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.inputContainer}>
        <label htmlFor="discountInput" className={styles.label}>
          Discount Code
        </label>
        <div className={styles.inputBox}>
          <Input
            value={discountCode}
            id="discountInput"
            onChange={e => {
              setDiscountCode(e.target.value);
              setErrorMessage("");
            }}
            className={styles.input}
          />
          {showButton && (
            <Button disabled={loading} className={styles.submitButton} onClick={validateCoupon}>
              Apply Coupon
            </Button>
          )}
        </div>
      </div>
      {couponData?.applyProductCoupon?.success === true && (
        <span className={styles.successMessage}>Coupon Applied</span>
      )}
      {errorMessage && <span className={styles.errorMessage}>{errorMessage}</span>}
    </div>
  );
};

export default DiscountCodeInput;
